// Client-Funktionen des Moduls smartview-kopf (crm/API.md, Abschnitt „smartview-kopf“):
// neuer Lead, CSV-Import, Massen-Mail, Massenbearbeitung, Export, Dialer-Verwaltung.
// Eigene Datei, damit api/index.ts unverändert bleibt.
import { http } from "./client";
import type { FeldWert, Lead } from "./types";

// --- Dubletten und neuer Lead ------------------------------------------------

export interface DublettenTreffer {
  lead_id: string;
  name: string;
  status_label: string | null;
  gruende: { art: "telefon" | "email" | "name"; wert: string | null }[];
}

export interface NeuerLeadEingabe {
  firma: string;
  kontakt_name?: string | null;
  telefon?: string | null;
  email?: string | null;
  website?: string | null;
  status_id?: string | null;
  bearbeiter_id?: string | null;
  dubletten_ignorieren?: boolean;
}

export const leadNeuApi = {
  dubletten: (e: { firma?: string; telefon?: string; email?: string }) => http.post<{ data: DublettenTreffer[] }>("/api/lead-neu/dubletten", e),
  anlegen: (e: NeuerLeadEingabe) => http.post<{ lead: Lead }>("/api/lead-neu", e),
};

// --- CSV-Import ------------------------------------------------------------------

export interface ImportZeile {
  firma?: string;
  kontakt_name?: string;
  telefon?: string;
  email?: string;
  website?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  status?: string;
  beschreibung?: string;
  custom?: Record<string, string>;
}

export interface ImportPruefung {
  index: number;
  gueltig: boolean;
  fehler: string[];
  warnungen: string[];
  telefon_e164: string | null;
  email_norm: string | null;
  dubletten: DublettenTreffer[];
}

export interface ImportErgebnisZeile {
  index: number;
  zeile: number;
  firma: string | null;
  ergebnis: "angelegt" | "uebersprungen" | "fehler";
  lead_id?: string | null;
  fehler?: string;
}

export const leadImportApi = {
  pruefen: (zeilen: ImportZeile[]) => http.post<{ data: ImportPruefung[] }>("/api/lead-import/pruefen", { zeilen }),
  anlegen: (e: { zeilen: ImportZeile[]; nummern: number[]; dubletten: "ueberspringen" | "trotzdem"; standard_status_id?: string | null; bearbeiter_id?: string | null }) =>
    http.post<{ data: ImportErgebnisZeile[]; angelegt: number; uebersprungen: number; fehler: number }>("/api/lead-import/anlegen", e),
};

// --- Ziel (Smart View, Auswahl oder Leads-Liste) ----------------------------------

export interface Ziel {
  view_id?: string;
  lead_ids?: string[];
  liste?: boolean;
  q?: string;
  status_id?: string[];
}

export interface ZielAntwort {
  lead_ids: string[];
  anzahl: number;
  gesamt: number | null;
  gekappt: boolean;
  maximal: number;
}

export interface Aktion {
  status_id?: string | null;
  bearbeiter_id?: string | null;
  custom?: Record<string, FeldWert>;
}

export interface BearbeitungErgebnis {
  lead_id: string;
  name?: string | null;
  ergebnis: "geaendert" | "fehler";
  fehler?: string;
}

export interface DialerInfo {
  smart_view: { id: string; name: string };
  anrufe_heute: number | null;
  ruft_gerade_an: { user_id: string; name: string; angerufen: number; seit: string; ich: boolean }[];
  klingeldauer: { standard: number; min: number; max: number; kurz: number };
}

export const massenApi = {
  ziel: (ziel: Ziel) => http.post<ZielAntwort>("/api/massenbearbeitung/ziel", ziel),
  ausfuehren: (lead_ids: string[], aktion: Aktion) =>
    http.post<{ data: BearbeitungErgebnis[]; geaendert: number; fehler: number }>("/api/massenbearbeitung/ausfuehren", { lead_ids, aktion, bestaetigt: true }),
  exportCsv: (ziel: Ziel) => http.post<{ csv: string; anzahl: number; gesamt: number | null; gekappt: boolean }>("/api/massenbearbeitung/export", ziel),
  dialerInfo: (viewId: string) => http.get<DialerInfo>("/api/massenbearbeitung/dialer-info", { view_id: viewId }),
};

// --- Massen-Mail -----------------------------------------------------------------------

export interface MailStatus {
  tageslimit: number;
  gesendet_heute: number;
  uebrig: number;
  pause_sekunden: number;
  konto: boolean;
  max_empfaenger: number;
}

export interface MailEmpfaenger {
  lead_id: string;
  name: string;
  status_label: string | null;
  contact_id: string | null;
  kontakt: string | null;
  email: string | null;
  problem: "keine_mail" | "gesperrt" | "abgemeldet" | "kein_kontakt" | null;
  problem_text: string | null;
}

export interface MailVorschau {
  lead_id: string;
  name: string;
  an: string[];
  betreff: string;
  html: string;
  text: string;
  fehlende: string[];
  von: string | null;
}

export interface MailSendeErgebnis {
  lead_id: string;
  name: string;
  email: string | null;
  ergebnis: "gesendet" | "uebersprungen" | "fehler";
  grund?: string | null;
  status?: MailStatus;
}

export const massenMailApi = {
  status: () => http.get<MailStatus>("/api/massenmail/status"),
  empfaenger: (ziel: Ziel) =>
    http.post<{ data: MailEmpfaenger[]; anzahl: number; sendbar: number; probleme: Record<string, number>; gesamt: number | null; gekappt: boolean; status: MailStatus }>(
      "/api/massenmail/empfaenger",
      { ziel },
    ),
  vorschau: (e: { lead_id: string; betreff: string; text: string }) => http.post<MailVorschau>("/api/massenmail/vorschau", e),
  senden: (e: { lead_id: string; betreff: string; text: string; lauf_id: string; datei_ids?: string[] }) =>
    http.post<MailSendeErgebnis>("/api/massenmail/senden", { ...e, bestaetigt: true }),
};
